import { useCallback, useEffect, useRef, useState } from "react";
import { pingReadingSession, startReadingSession, trackSessionEvent } from "../../lib/api";

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

export function useReadingTracker(slug, lieu, ready) {
  const [session, setSession] = useState({ id: null, placement: null });
  const st = useRef({ active: 0, maxScroll: 0, completed: false });

  useEffect(() => {
    if (!ready) return;
    const key = `cled_rs_${slug}_${lieu || ""}`;
    let sid = sessionStorage.getItem(key);
    if (!sid) { sid = newId(); sessionStorage.setItem(key, sid); }
    startReadingSession({ session_id: sid, slug, lieu: lieu || null })
      .then((r) => setSession({ id: sid, placement: r.placement }))
      .catch(() => {});
  }, [slug, lieu, ready]);

  const send = useCallback((beacon = false) => {
    if (!session.id) return;
    const { active, maxScroll, completed } = st.current;
    pingReadingSession(session.id, { duration_sec: Math.round(active), max_scroll: maxScroll, completed }, beacon);
  }, [session.id]);

  useEffect(() => {
    if (!session.id) return;
    const tick = setInterval(() => { if (document.visibilityState === "visible") st.current.active += 1; }, 1000);
    const flush = setInterval(() => send(), 10000);
    const onVisibility = () => document.visibilityState === "hidden" && send(true);
    const onPageHide = () => send(true);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      clearInterval(tick); clearInterval(flush);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      send(true);
    };
  }, [session.id, send]);

  const markScroll = useCallback((p) => { st.current.maxScroll = Math.max(st.current.maxScroll, Math.round(p * 100) / 100); }, []);
  const markCompleted = useCallback(() => {
    if (st.current.completed) return;
    st.current.completed = true;
    send();
  }, [send]);
  const track = useCallback((type) => session.id && trackSessionEvent(session.id, type), [session.id]);

  return { sessionId: session.id, placement: session.placement, markScroll, markCompleted, track };
}
